import { computed, Inject, Injectable, signal, Signal } from '@angular/core';
import { IChatbotApiService } from './chatbot-api/chatbot-api.service.interface';
import { IMessageService } from './message/message.service.interface';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  expand,
  finalize,
  last,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { Message } from './message/message.model';
import { v4 as uuidv4 } from 'uuid';
import { AppNetworkService } from '../shared/network/app-network.service';
import { ConnectionStatus } from '../shared/network/connection-status.model';
import { SessionResponse } from './chatbot-api/session-response.model';
import { PromptQuotaStatus } from './chatbot-api/prompt-quota-status.model';
import { ChatbotMode } from './chatbot-api/chatbot-mode.model';

/**
 * ChatbotService manages the flow of chatbot interactions.
 *
 * Responsibilities:
 * - Constructs and emits user and chatbot messages.
 * - Determines whether to process messages online or offline.
 * - Streams chatbot responses from the LLM API.
 * - Updates message state incrementally as data arrives.
 * - Handles session lifecycle and cancellation via ChatEventService.
 * - Gracefully manages errors and interruptions in the response stream.
 *
 * Dependencies:
 * - IChatbotApiService: for streaming chatbot responses.
 * - IMessageService: for pushing messages to the UI or store.
 * - ChatEventService: for session management and cancellation signals.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private _maxChars: number = 600;
  private _maxRetries: number = 3;
  private _retryDelays = [1000, 3000, 9000];
  private _canAddEquation: boolean = true;
  private _disclaimerMessage: string = "I'm your learning AI companion. Please don't share any personal, sensitive, or confidential information with me. All my answers are inspired by your E-books. Always verify them!";
  private _isChatbotModeActive: boolean = true;
  private _chatbotModes: ChatbotMode[] = [];

  private _lastSessionStatus = signal<SessionResponse | null>(null);
  private _isNetworkOnline = signal<boolean>(false);
  private _isConnectionEstablished = signal<boolean>(false);

  private _sessionsQuotaUpdater: Subject<SessionsQuotaStatus> = new Subject();
  public sessionsQuota$: Observable<SessionsQuotaStatus> = this._sessionsQuotaUpdater.asObservable();

  private _promptsQuotaUpdater: Subject<PromptQuotaStatus> = new Subject<PromptQuotaStatus>();
  public promptQuota$: Observable<PromptQuotaStatus> = this._promptsQuotaUpdater.asObservable();

  private _isResponseReady: Subject<boolean> = new Subject();
  public isResponseReady$: Observable<boolean> = this._isResponseReady.asObservable();

  private _isChatInProgress: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isChatInProgress$: Observable<boolean> = this._isChatInProgress.asObservable();

  private _activeSessionId: string | null = null;
  private _activeStreamAbortSubject: Subject<void> = new Subject();

  private _isHistoryCollapsedByDefault: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public isHistoryCollapsedByDefault$: Observable<boolean> = this._isHistoryCollapsedByDefault.asObservable();

  private _maxInputChars: BehaviorSubject<number> = new BehaviorSubject(this._maxChars);
  public maxInputChars$: Observable<number> = this._maxInputChars.asObservable();

  private _isHistoryAllowed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isHistoryAllowed$: Observable<boolean> = this._isHistoryAllowed.asObservable();

  private _chatbotSessionId: number | null = null;

  public canStartNewSession: Signal<boolean> = computed(() => {
    const status = this._lastSessionStatus();
    return this._isConnectionEstablished() && this._isNetworkOnline() && !!status && !status.limitReached;
  });

  constructor(
    @Inject('ChatbotApiService') private _chatbotApiService: IChatbotApiService,
    @Inject('MessageService') private _messageService: IMessageService,
    private _appNetworkService: AppNetworkService
  ) {
    this._appNetworkService.online$.subscribe((isOnline) => {
      this._isNetworkOnline.set(isOnline);
    });
  }

  /**
   * Attempts to establish a connection to the chatbot backend.
   *
   * - Resets connection state and immediately emits an IN_PROGRESS status.
   * - Retries the health check up to `_maxRetries` times with increasing delays.
   * - On success, reuses the existing session if one is active, or generates a new one.
   * - On failure, emits a FAILURE status with a manual retry prompt.
   *
   * @returns Observable emitting connection state updates until the attempt completes.
   */
  public establishConnection(): Observable<ChatbotInitialConnection> {
    const MAX_RETRIES = this._maxRetries;
    const RETRY_DELAYS = this._retryDelays;
    let retryCount = 0;

    this._isConnectionEstablished.set(false);

    let initialStatus = this.buildConnectionSnapshot('IN_PROGRESS', 'Trying to connect you... almost there!', 0, false);

    // emit initial status - connecting...
    const resultSubject = new BehaviorSubject<ChatbotInitialConnection>(initialStatus);
    resultSubject.next(initialStatus);

    this._chatbotApiService
      .healthCheck()
      .pipe(
        catchError(_ => of(false)),

        // keep retrying until true or attempts exceeded
        expand(res => {
          if (res === true || retryCount >= MAX_RETRIES) {
            return EMPTY; // completes the stream naturally, will no longer call 'expand'
          }
          const delay = RETRY_DELAYS[retryCount];
          retryCount++;
          return timer(delay).pipe(
            tap(() => this.emitRetryMessage(retryCount, resultSubject)),
            switchMap(() => this._chatbotApiService.healthCheck()),
            catchError(() => of(false))
          );
        }),
        last(),

        // will be called when all attempts are made or when connection is established
        switchMap(res => {
          if (!res) {
            return of(false);
          }
          return this._activeSessionId
            ? this.reconnectExistingSession(resultSubject, retryCount)
            : this.connectNewSession(resultSubject, retryCount);
        }),
        finalize(() => {
          if (!this._isConnectionEstablished()) {
            // all attempts used and still not connected
            resultSubject.next(
              this.buildConnectionSnapshot(
                'FAILURE',
                "Couldn't reconnect - we gave it our best shot. Reconnect manually.",
                retryCount,
                true
              )
            );
          }
        })
      )
      .subscribe();

    return resultSubject.asObservable();
  }

  /**
   * Orchestrates the full message flow for a user interaction.
   *
   * - Emits a user message.
   * - If offline, returns the user message immediately.
   * - If online, emits a "Typing..." chatbot placeholder and streams the response.
   *
   * @param userMessageContent - The user's input message.
   * @param chatbotModeCode - The active chatbot mode identifier to pass to the API.
   * @returns Observable emitting the chatbot's response message.
   */
  public handleMessageFlow(userMessageContent: string, chatbotModeCode: string): Observable<Message> {
    this._isChatInProgress.next(true);
    // create message object
    const userMessage = this.createUserMessage(
      userMessageContent,
      this._isNetworkOnline(),
      this._isConnectionEstablished()
    );

    // push message to local list of sent messages
    this._messageService.pushMessage(userMessage);

    // stop if not connected
    if (!this._isNetworkOnline || !this._isConnectionEstablished) {
      this._isChatInProgress.next(false);
      return of(userMessage);
    }

    // create "Typing..." message
    const chatbotMessage = this.createTypingMessage(userMessage);
    this._messageService.pushMessage({ ...chatbotMessage });

    // send request to chatbotAPI
    return this.streamChatbotResponse(userMessageContent, chatbotMessage, chatbotModeCode);
  }

  /**
   * Generates a new session ID via the API and updates local session state.
   *
   * - Closes any active stream before switching sessions.
   * - Resets the prompt quota for the newly created session.
   *
   * @returns Observable emitting the full session response from the API.
   */
  public generateSessionId(): Observable<SessionResponse> {
    return this._chatbotApiService.generateSessionId().pipe(
      tap((r) => {
        this.closeActiveStream();
        this._activeSessionId = r.session_id;
        this._chatbotSessionId = r.chatbotSessionId;
        this._lastSessionStatus.set(r);
        this._sessionsQuotaUpdater.next(r);
        this._promptsQuotaUpdater.next({
          limitReached: false,
          sessionId: r.session_id,
          showWarning: false,
        });
      })
    );
  }

  /**
   * Emits a retry status update to the connection subject.
   *
   * @param attempt - The current retry attempt number (1-based).
   * @param resultSubject - The subject to emit the updated connection snapshot to.
   */
  private emitRetryMessage(attempt: number, resultSubject: Subject<ChatbotInitialConnection>): void {
    const messages = [
      'Hang on... Connecting... Attempt 1',
      'Still trying... Attempt 2',
      'Almost there... Attempt 3',
    ];
    if (attempt >= 1 && attempt <= messages.length) {
      resultSubject.next(
        this.buildConnectionSnapshot('IN_PROGRESS', messages[attempt - 1], attempt, false)
      );
    }
  }

  /**
   * Builds a `ChatbotInitialConnection` snapshot from the current instance state.
   *
   * @param statusCode - The connection status code.
   * @param statusMessage - A human-readable description of the current status.
   * @param retryCount - The number of connection retries attempted so far.
   * @param canRetry - Whether the user is allowed to manually retry the connection.
   * @returns A `ChatbotInitialConnection` object reflecting the current state.
   */
  private buildConnectionSnapshot(statusCode: ConnectionStatus['statusCode'], statusMessage: string, retryCount: number, canRetry: boolean): ChatbotInitialConnection {
    return {
      isConnected: this._isConnectionEstablished(),
      maxChars: this._maxChars,
      status: { statusCode, statusMessage, retryCount, canRetry },
      canAddEquation: this._canAddEquation,
      disclaimerMessage: this._disclaimerMessage,
      isChatbotModeActive: this._isChatbotModeActive,
      chatbotModes: this._chatbotModes,
    };
  }

  /**
   * Handles reconnection when an active session ID already exists.
   *
   * - Marks the connection as established and emits a success snapshot.
   * - Refreshes the prompt quota for the existing session.
   *
   * @param resultSubject - The subject to emit the updated connection snapshot to.
   * @param retryCount - The number of retries that were attempted.
   * @returns Observable emitting `true` on success.
   */
  private reconnectExistingSession(resultSubject: Subject<ChatbotInitialConnection>, retryCount: number): Observable<boolean> {
    this._isConnectionEstablished.set(true);
    resultSubject.next(
      this.buildConnectionSnapshot('SUCCESS', "You're connected!", retryCount, false)
    );
    this._chatbotApiService
      .getPromptsQuota(this._activeSessionId!)
      .subscribe((p) => {
        this._promptsQuotaUpdater.next(p);
      });
    return of(true);
  }

  /**
   * Handles connection setup by generating a new session via the API.
   *
   * - Updates all session-related instance fields from the API response.
   * - Emits a success snapshot and notifies session quota and history state observers.
   *
   * @param resultSubject - The subject to emit the updated connection snapshot to.
   * @param retryCount - The number of retries that were attempted.
   * @returns Observable emitting `true` on success, or `false` if session generation fails.
   */
  private connectNewSession(resultSubject: Subject<ChatbotInitialConnection>, retryCount: number): Observable<boolean> {
    return this._chatbotApiService.generateSessionId().pipe(
      tap(response => {
        this._isConnectionEstablished.set(true);
        this._maxChars = response.inputMaxChars;
        this._activeSessionId = response.session_id;
        this._chatbotSessionId = response.chatbotSessionId;
        this._canAddEquation = response.canAddEquation;
        this._disclaimerMessage = response.aiMessage;
        this._isChatbotModeActive = response.isChatbotModeActive;
        this._chatbotModes = response.chatbotModes;

        this._isHistoryAllowed.next(response.isHistoryAllowed);

        this._isHistoryCollapsedByDefault.next(response.isHistoryCollapsedByDefault);
        this._maxInputChars.next(response.inputMaxChars);
        this._lastSessionStatus.set(response);
        this._sessionsQuotaUpdater.next(response);

        resultSubject.next(
          this.buildConnectionSnapshot('SUCCESS', "You're connected!", retryCount, false)
        );
      }),
      map(() => true),
      catchError(err => {
        console.error('generateSessionId failed:', err);
        return of(false);
      })
    );
  }

  /**
   * Constructs a user message object.
   *
   * @param content - The message content.
   * @param isOnline - Network status.
   * @param isConnected - Backend connection status.
   * @returns A Message object representing the user's input.
   */
  private createUserMessage(content: string, isOnline: boolean, isConnected: boolean): Message {
    return {
      id: uuidv4(),
      sender: 'user',
      content,
      timestamp: new Date(),
      isOffline: !isOnline || !isConnected,
    };
  }

  /**
   * Constructs a placeholder chatbot message indicating it's typing.
   *
   * @param parent - The parent user message.
   * @returns A Message object representing the chatbot's initial response.
   */
  private createTypingMessage(parent: Message): Message {
    return {
      id: uuidv4(),
      sender: 'chatbot',
      content: 'Typing...',
      timestamp: new Date(),
      isChatbotResponseInterrupted: false,
      parentMessage: parent,
    };
  }

  /**
   * Streams the chatbot response from the LLM API and updates the message incrementally.
   *
   * - Updates the chatbot message content with each arriving chunk.
   * - Handles cancellation via the active stream abort subject.
   * - On error, marks the message as interrupted and preserves any partial content.
   *
   * @param userContent - The user's message content.
   * @param chatbotMessage - The chatbot placeholder message to update incrementally.
   * @param chatbotModeCode - The active chatbot mode identifier to pass to the API.
   * @returns Observable emitting the updated chatbot message.
   */
  private streamChatbotResponse(userContent: string, chatbotMessage: Message, chatbotModeCode: string): Observable<Message> {
    // to reduce request size (long equations cannot be sent when using fetch)
    let contentWithoutEquationHTML = this.removeEquationInnerHTML(userContent);
    let isFirstChunk = true;
    return this._chatbotApiService
      .streamLLMResponse(contentWithoutEquationHTML, this._activeSessionId!, chatbotModeCode)
      .pipe(
        takeUntil(this._activeStreamAbortSubject.asObservable()),
        tap((r) => {
          if (isFirstChunk) {
            this._chatbotApiService
              .getPromptsQuota(this._activeSessionId!)
              .subscribe((p) => {
                this._promptsQuotaUpdater.next(p);
              });
          }
        }),
        map((data) => {
          chatbotMessage.content = isFirstChunk
            ? data
            : chatbotMessage.content + data;
          isFirstChunk = false;
          this._messageService.pushMessage({ ...chatbotMessage });
          this.setIsResponseReady(true);
          return chatbotMessage;
        }),
        catchError(() => {
          chatbotMessage.isChatbotResponseInterrupted = true;
          chatbotMessage.content = isFirstChunk ? '' : chatbotMessage.content;
          this._messageService.pushMessage({ ...chatbotMessage });
          this.setIsResponseReady(true);
          return of(chatbotMessage);
        },
        ),
        finalize(() => {
          this._isChatInProgress.next(false);
        })
      );
  }

  /**
   * Strips inner HTML from all `<sabis-equation>` elements in the content string.
   * Used to reduce request payload size when equations are present.
   *
   * @param userContent - The raw HTML string from the user's input.
   * @returns The sanitized HTML string with equation inner content removed.
   */
  private removeEquationInnerHTML(userContent: string): string {
    let SABISEquationElementName: string = 'sabis-equation';
    const parser = new DOMParser();
    const doc = parser.parseFromString(userContent, 'text/html');
    const equationElements = doc.querySelectorAll(SABISEquationElementName);
    equationElements.forEach((el) => {
      el.innerHTML = '';
    });
    return doc.body.innerHTML;
  }

  /**
   * Cancels any in-progress LLM response stream by completing the abort subject
   * and replacing it with a fresh one for the next request.
   */
  public closeActiveStream(): void {
    this._activeStreamAbortSubject.next();
    this._activeStreamAbortSubject = new Subject();
  }

  /**
   * Cancels the active stream and clears the session ID.
   * Use this when fully ending a session rather than just stopping a stream.
   */
  public closeActiveSession(): void {
    this.closeActiveStream();
    this._activeSessionId = null;
  }

  /**
   * Sets the active session to an existing session (e.g. when loading from history).
   * Does not create a new session; use generateSessionId() for that.
   *
   * @param llmSessionId - The session ID to restore.
   * @param promptQuota - The prompt quota state associated with the restored session.
   */
  public loadSession(llmSessionId: string, promptQuota: PromptQuotaStatus): void {
    this.closeActiveStream();
    this._activeSessionId = llmSessionId;
    this._promptsQuotaUpdater.next(promptQuota);
  }

  /**
   * Returns the current active session ID, or `null` if no session is active.
   *
   * @returns The active session ID string, or `null`.
   */
  public getActiveSessionId(): string | null {
    return this._activeSessionId;
  }

  /**
   * Returns the current active chatbot session ID, or `null` if no session is active.
   * @returns The active chatbot session ID number, or `null`.
   */
  public getActiveChatbotSessionId(): number | null {
    return this._chatbotSessionId;
  }

  /**
   * Signals whether a chatbot response chunk has been received and is ready to display.
   *
   * @param value - `true` when a response chunk arrives; `false` to reset the state.
   */
  public setIsResponseReady(value: boolean): void {
    this._isResponseReady.next(value);
  }

  /**
   * Detects whether the current device is a tablet based on the user agent string.
   *
   * @returns `true` if the device is identified as a tablet; `false` otherwise.
   */
  public isTablet(): boolean {
    return /iPad|Android(?!.*Mobile)|Tablet|Touch/.test(navigator.userAgent);
  }

  /**
   * Returns the maximum number of characters allowed in the input.
   *
   * @returns The max allowed input characters count.
   */
  public getMaxInputChars(): number {
    return this._maxInputChars.getValue();
  }

  public getConnectionStatus(): boolean { 
    return this._isConnectionEstablished();
  }

}

export interface ChatbotInitialConnection {
  isConnected: boolean;
  maxChars: number;
  status: ConnectionStatus;
  canAddEquation: boolean;
  disclaimerMessage: string;
  isChatbotModeActive: boolean;
  chatbotModes: ChatbotMode[];
}

export interface SessionsQuotaStatus {
  /** Indicates whether a warning should be shown when sessions are close to expiration. */
  showWarning: boolean;

  /** Number of remaining sessions available to the user for the current day. */
  remainingSessionCount: number;

  /** Indicates whether the user has exhausted all allowed sessions for the day. */
  limitReached: boolean;

  /** Indicates if this is the last session for today */
  isLastSession: boolean;
}

export enum PageLinks {
  EbookPageLink = 'ebook-page-link',
}
